/**
 * Mermaid state diagram parser.
 * Falls back to regex parsing if mermaid.js API is unavailable.
 */

import type { IRGraph, IRNode, IREdge, IRLane, Direction } from '../types';
import { NodeShape, createEmptyGraph } from '../types';
import { SHAPE_REGISTRY } from '../registry';

let mermaidInstance: typeof import('mermaid')['default'] | null = null;

async function getMermaid() {
  if (!mermaidInstance) {
    const mod = await import('mermaid');
    mermaidInstance = mod.default;
    mermaidInstance.initialize({ startOnLoad: false, logLevel: 5 as any });
  }
  return mermaidInstance;
}

export async function parseMermaidState(text: string): Promise<IRGraph> {
  try {
    return await parseMermaidApi(text);
  } catch {
    return parseRegex(text);
  }
}

async function parseMermaidApi(text: string): Promise<IRGraph> {
  const mermaid = await getMermaid();
  const diagram = await mermaid.mermaidAPI.getDiagramFromText(text);
  const db = (diagram as any).db;

  const graph = createEmptyGraph('TD', 'state');

  // mermaid state db uses getStates() and getRelations()
  const states: Map<string, any> = db.getStates?.() ?? new Map();
  const relations: any[] = db.getRelations?.() ?? [];

  const startTargets = new Set<string>();
  const endSources = new Set<string>();

  // Scan relations for [*] / start/end pseudo-state references
  // Also detect pseudo-states by checking if the ID is not in the states map
  for (const rel of relations) {
    const src = String(rel.id1 ?? '');
    const tgt = String(rel.id2 ?? '');
    const srcIsPseudo = isStartPseudoState(src) || !states.has(src);
    const tgtIsPseudo = isEndPseudoState(tgt) || !states.has(tgt);
    if (srcIsPseudo) startTargets.add(tgt);
    if (tgtIsPseudo) endSources.add(src);
  }

  // Build nodes from states
  for (const [id, state] of states) {
    if (id === '[*]') continue;
    const shape = classifyStateShape(state, startTargets, endSources);
    const entry = SHAPE_REGISTRY[shape];
    const label = state.descriptions?.[0] ?? state.description ?? id;
    const node: IRNode = {
      id,
      label: typeof label === 'string' ? label : id,
      shape,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
      meta: { stateType: state.type },
    };
    graph.nodes.set(id, node);
  }

  // If we got no states from the API, fall back to regex
  if (graph.nodes.size === 0) {
    return parseRegex(text);
  }

  // Build edges
  let edgeCounter = 0;
  for (const rel of relations) {
    const srcId = String(rel.id1 ?? '');
    const tgtId = String(rel.id2 ?? '');
    const srcIsPseudo = isStartPseudoState(srcId) || !graph.nodes.has(srcId);
    const tgtIsPseudo = isEndPseudoState(tgtId) || !graph.nodes.has(tgtId);
    const src = srcIsPseudo ? findStartNode(graph, startTargets) : srcId;
    const tgt = tgtIsPseudo ? findEndNode(graph, endSources) : tgtId;
    if (!src || !tgt) continue;

    edgeCounter++;
    const edge: IREdge = {
      id: `e${edgeCounter}`,
      source: src,
      target: tgt,
      label: rel.relationTitle || undefined,
    };
    graph.edges.push(edge);
  }

  addOrphanLane(graph);
  return graph;
}

/** Check if an ID represents a start pseudo-state ([*], root_start, etc.) */
function isStartPseudoState(id: string): boolean {
  if (id === '[*]') return true;
  // mermaid uses identifiers like 'root_start' for [*] at the start
  if (/start/i.test(id) && /root/i.test(id)) return true;
  return false;
}

/** Check if an ID represents an end pseudo-state */
function isEndPseudoState(id: string): boolean {
  if (id === '[*]') return true;
  // mermaid uses identifiers like 'root_end' for [*] at the end
  if (/end/i.test(id) && /root/i.test(id)) return true;
  return false;
}

function classifyStateShape(
  state: any,
  _startTargets: Set<string>,
  _endSources: Set<string>,
): NodeShape {
  const type = state.type;
  if (type === 'choice') return NodeShape.Decision;
  if (type === 'fork') return NodeShape.ForkBar;
  if (type === 'join') return NodeShape.JoinBar;
  return NodeShape.Process;
}

function findStartNode(graph: IRGraph, startTargets: Set<string>): string | undefined {
  // Find a dedicated start node, or create one
  for (const [id, node] of graph.nodes) {
    if (node.shape === NodeShape.StartCircle) return id;
  }
  // Create a start node
  const entry = SHAPE_REGISTRY[NodeShape.StartCircle];
  const startId = '__start__';
  graph.nodes.set(startId, {
    id: startId,
    label: '',
    shape: NodeShape.StartCircle,
    width: entry.defaultWidth,
    height: entry.defaultHeight,
  });
  return startId;
}

function findEndNode(graph: IRGraph, endSources: Set<string>): string | undefined {
  for (const [id, node] of graph.nodes) {
    if (node.shape === NodeShape.EndCircle) return id;
  }
  const entry = SHAPE_REGISTRY[NodeShape.EndCircle];
  const endId = '__end__';
  graph.nodes.set(endId, {
    id: endId,
    label: '',
    shape: NodeShape.EndCircle,
    width: entry.defaultWidth,
    height: entry.defaultHeight,
  });
  return endId;
}

// ─── Regex fallback ──────────────────────────────────────────────

const TRANSITION_RE =
  /(\[\*\]|[\w]+)\s*-->\s*(\[\*\]|[\w]+)\s*(?::\s*(.+))?/g;
const COMPOSITE_START = /state\s+(\w+)\s*\{/;
const COMPOSITE_END = /^\s*\}\s*$/;
const CHOICE_RE = /state\s+(\w+)\s+<<choice>>/;
const FORK_RE = /state\s+(\w+)\s+<<fork>>/;
const JOIN_RE = /state\s+(\w+)\s+<<join>>/;
const DIRECTION_RE = /direction\s+(TB|TD|LR|RL|BT)/;
const STATE_DESCRIPTION_RE = /^state\s+(\w+)\s*:\s*(.+)$/;
const SHORTHAND_DESC_RE = /^(\w+)\s*:\s*(.+)$/;
const NOTE_SINGLE_RE = /^note\s+(right|left)\s+of\s+(\w+)\s*:\s*(.+)$/i;
const NOTE_BLOCK_START_RE = /^note\s+(right|left)\s+of\s+(\w+)\s*$/i;
const NOTE_BLOCK_END_RE = /^end\s+note$/i;

/**
 * Expand chained transitions: `A --> B --> C : label`
 * becomes two lines: `A --> B` and `B --> C : label`
 */
function expandChainedTransitions(line: string): string[] {
  // Match all `ID --> ID` segments in the line
  const chainRe = /(\[\*\]|[\w]+)\s*-->\s*/g;
  const ids: string[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = chainRe.exec(line)) !== null) {
    ids.push(m[1]);
    lastIndex = chainRe.lastIndex;
  }

  if (ids.length < 2) return [line];

  // Capture the trailing target (and optional label) after the last `-->`
  const trailing = line.slice(lastIndex).trim();
  const trailingMatch = /^(\[\*\]|[\w]+)\s*(?::\s*(.+))?$/.exec(trailing);
  if (!trailingMatch) return [line];

  ids.push(trailingMatch[1]);
  const label = trailingMatch[2];

  // Build individual transition lines
  const result: string[] = [];
  for (let i = 0; i < ids.length - 1; i++) {
    const isLast = i === ids.length - 2;
    if (isLast && label) {
      result.push(`${ids[i]} --> ${ids[i + 1]} : ${label}`);
    } else {
      result.push(`${ids[i]} --> ${ids[i + 1]}`);
    }
  }
  return result;
}

function parseRegex(text: string): IRGraph {
  const rawLines = text.trim().split('\n');
  const graph = createEmptyGraph('TD', 'state');

  const nodeMap = new Map<string, { label: string; shape: NodeShape }>();
  const specialNodes = new Map<string, NodeShape>();
  const compositeStack: IRLane[] = [];
  const compositeNodeIds = new Set<string>(); // IDs that are composite containers
  const startTargets = new Set<string>();
  const endSources = new Set<string>();
  const descriptions = new Map<string, string>(); // nodeId → description
  const notes: Array<{ position: string; nodeId: string; text: string }> = [];
  let inNoteBlock = false;
  let noteBlockPos = '';
  let noteBlockNodeId = '';
  let noteBlockLines: string[] = [];

  // First pass: collect special annotations, composite states, descriptions, notes
  for (const rawLine of rawLines) {
    const line = rawLine.trim();

    // Note block continuation
    if (inNoteBlock) {
      if (NOTE_BLOCK_END_RE.test(line)) {
        notes.push({ position: noteBlockPos, nodeId: noteBlockNodeId, text: noteBlockLines.join('\n') });
        inNoteBlock = false;
        noteBlockLines = [];
      } else {
        noteBlockLines.push(line);
      }
      continue;
    }

    const dirMatch = DIRECTION_RE.exec(line);
    if (dirMatch) {
      graph.direction = dirMatch[1] as Direction;
      continue;
    }

    const choiceMatch = CHOICE_RE.exec(line);
    if (choiceMatch) {
      specialNodes.set(choiceMatch[1], NodeShape.Decision);
      continue;
    }

    const forkMatch = FORK_RE.exec(line);
    if (forkMatch) {
      specialNodes.set(forkMatch[1], NodeShape.ForkBar);
      continue;
    }

    const joinMatch = JOIN_RE.exec(line);
    if (joinMatch) {
      specialNodes.set(joinMatch[1], NodeShape.JoinBar);
      continue;
    }

    // Single-line note
    const noteSingleMatch = NOTE_SINGLE_RE.exec(line);
    if (noteSingleMatch) {
      notes.push({
        position: noteSingleMatch[1].toLowerCase(),
        nodeId: noteSingleMatch[2],
        text: noteSingleMatch[3].trim(),
      });
      continue;
    }

    // Note block start
    const noteBlockMatch = NOTE_BLOCK_START_RE.exec(line);
    if (noteBlockMatch) {
      inNoteBlock = true;
      noteBlockPos = noteBlockMatch[1].toLowerCase();
      noteBlockNodeId = noteBlockMatch[2];
      noteBlockLines = [];
      continue;
    }

    // `state X : Description` syntax
    const stateDescMatch = STATE_DESCRIPTION_RE.exec(line);
    if (stateDescMatch) {
      descriptions.set(stateDescMatch[1], stateDescMatch[2].trim());
      // Don't continue — this might also be parsed for composite start, etc.
      // But STATE_DESCRIPTION_RE won't match composite start (no `{`), so continue
      continue;
    }

    const compMatch = COMPOSITE_START.exec(line);
    if (compMatch) {
      const parentLane = compositeStack.length > 0 ? compositeStack[compositeStack.length - 1] : null;
      const lane: IRLane = {
        id: compMatch[1],
        title: compMatch[1],
        nodeIds: [],
        x: 0, y: 0, width: 0, height: 0,
        parentLaneId: parentLane?.id,
      };
      graph.lanes.push(lane);
      compositeStack.push(lane);
      // Also register composite as a node so it can be used in transitions
      compositeNodeIds.add(compMatch[1]);
      continue;
    }

    if (COMPOSITE_END.test(line)) {
      compositeStack.pop();
      continue;
    }

    // Shorthand description: `NodeId : Some text` (only if not a transition)
    if (!line.includes('-->')) {
      const shorthandMatch = SHORTHAND_DESC_RE.exec(line);
      if (shorthandMatch && !line.startsWith('state') && !line.startsWith('note')) {
        descriptions.set(shorthandMatch[1], shorthandMatch[2].trim());
        continue;
      }
    }

    // Expand chained transitions and collect them
    const expanded = expandChainedTransitions(line);
    for (const expLine of expanded) {
      TRANSITION_RE.lastIndex = 0;
      let tm: RegExpExecArray | null;
      while ((tm = TRANSITION_RE.exec(expLine)) !== null) {
        const src = tm[1];
        const tgt = tm[2];
        if (src === '[*]') startTargets.add(tgt);
        if (tgt === '[*]') endSources.add(src);

        // Register regular nodes
        for (const id of [src, tgt]) {
          if (id === '[*]' || nodeMap.has(id)) continue;
          const shape = specialNodes.get(id) ?? NodeShape.Process;
          nodeMap.set(id, { label: id, shape });
          const currentComposite = compositeStack.length > 0 ? compositeStack[compositeStack.length - 1] : null;
          if (currentComposite && !compositeNodeIds.has(id)) {
            currentComposite.nodeIds.push(id);
          }
        }
      }
    }
  }

  // Store notes in graph meta
  if (notes.length > 0) {
    (graph as any).meta = { notes };
  }

  // Apply descriptions to nodeMap entries
  for (const [id, desc] of descriptions) {
    const existing = nodeMap.get(id);
    if (existing) {
      existing.label = desc;
    } else {
      // Description for a node not yet seen in transitions — register it
      nodeMap.set(id, { label: desc, shape: specialNodes.get(id) ?? NodeShape.Process });
    }
  }

  // Add start node if we have [*] --> transitions
  if (startTargets.size > 0) {
    const entry = SHAPE_REGISTRY[NodeShape.StartCircle];
    nodeMap.set('__start__', { label: '', shape: NodeShape.StartCircle });
    graph.nodes.set('__start__', {
      id: '__start__',
      label: '',
      shape: NodeShape.StartCircle,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
    });
  }

  // Add end node if we have --> [*] transitions
  if (endSources.size > 0) {
    const entry = SHAPE_REGISTRY[NodeShape.EndCircle];
    nodeMap.set('__end__', { label: '', shape: NodeShape.EndCircle });
    graph.nodes.set('__end__', {
      id: '__end__',
      label: '',
      shape: NodeShape.EndCircle,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
    });
  }

  // Build IR nodes from nodeMap
  for (const [id, info] of nodeMap) {
    if (graph.nodes.has(id)) continue; // skip start/end already added
    const entry = SHAPE_REGISTRY[info.shape];
    const node: IRNode = {
      id,
      label: info.label,
      shape: info.shape,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
    };
    // Assign lane — but NOT if this ID is a composite container (it's the container, not a child)
    if (!compositeNodeIds.has(id)) {
      for (const lane of graph.lanes) {
        if (lane.nodeIds.includes(id)) {
          node.laneId = lane.id;
          break;
        }
      }
    }
    graph.nodes.set(id, node);
  }

  // Second pass: build edges from expanded lines
  for (const rawLine of rawLines) {
    const line = rawLine.trim();
    const expanded = expandChainedTransitions(line);
    for (const expLine of expanded) {
      TRANSITION_RE.lastIndex = 0;
      let tm: RegExpExecArray | null;
      while ((tm = TRANSITION_RE.exec(expLine)) !== null) {
        const src = tm[1] === '[*]' ? '__start__' : tm[1];
        const tgt = tm[2] === '[*]' ? '__end__' : tm[2];
        if (!graph.nodes.has(src) || !graph.nodes.has(tgt)) continue;

        graph.edges.push({
          id: `e${graph.edges.length + 1}`,
          source: src,
          target: tgt,
          label: tm[3]?.trim() || undefined,
        });
      }
    }
  }

  addOrphanLane(graph);
  return graph;
}

function addOrphanLane(graph: IRGraph): void {
  const laneNodeIds = new Set(graph.lanes.flatMap((l) => l.nodeIds));
  const orphans = [...graph.nodes.keys()].filter((id) => !laneNodeIds.has(id));
  if (orphans.length > 0) {
    const lane: IRLane = {
      id: '_general',
      title: 'General',
      nodeIds: orphans,
      x: 0, y: 0, width: 0, height: 0,
    };
    graph.lanes.push(lane);
    for (const id of orphans) {
      const node = graph.nodes.get(id);
      if (node) node.laneId = '_general';
    }
  }
}
